import { type VolumeBasedPriceV1 } from '@redteclab/api/clients/bully'

import { useGetDiscountPercentages } from './ProductVolumePricingModal/useGetDiscountPercentages'
import { useGetDiscountRanges } from './ProductVolumePricingModal/useGetDiscountRanges'

type UseVolumePricingBadgeDataResult = {
  discount?: number
  discountActivated?: boolean
  quantity?: number
}

/**
 * Gets information that will be displayed in the volume pricing badge:
 * 1. @returns discount: display information about percentage discount (ex. 6% Mengenrabatt aktiviert)
 * 2. @returns discountActivated: determines if the quantity is lower than the first threshold, so we can display information about first threshold and how to achieve it (ex. ab 3 Stuck 6% sparen)
 * 3. @returns quantity
 */
export const useVolumePricingBadgeData = (
  selectedQuantity: number,
  volumeBasedPrices: VolumeBasedPriceV1[],
): UseVolumePricingBadgeDataResult => {
  const ranges = useGetDiscountRanges(volumeBasedPrices)

  const { currentDiscountPercentage, firstDiscountPercentage } =
    useGetDiscountPercentages(ranges, selectedQuantity)

  const firstMinThreshold = ranges[1]?.rangeStart ?? 0

  if (selectedQuantity < firstMinThreshold) {
    return {
      discount: firstDiscountPercentage,
      discountActivated: false,
      quantity: firstMinThreshold,
    }
  }

  return {
    discount: currentDiscountPercentage,
    discountActivated: true,
    quantity: selectedQuantity,
  }
}
